import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  CreditCard,
  DollarSign,
  FileText,
  PiggyBank,
  Receipt,
  TrendingUp,
} from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { SectionHeader } from '@/components/SectionHeader'
import { SectionLink } from '@/components/SectionLink'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BarChart } from '@/components/charts/BarChart'
import { LineChart } from '@/components/charts/LineChart'
import { DoughnutChart } from '@/components/charts/DoughnutChart'
import { CATEGORY_PALETTE, CHART_COLORS } from '@/components/charts/palette'
import { formatCurrency, formatDate } from '@/lib/locale'
import { cn } from '@/lib/cn'
import {
  useBudgetDashboardSummary,
  useDashboardSummary,
  useInvalidateDashboard,
  usePendingDetails,
} from '@/features/overview/queries'
import { resolvePeriodWindow } from '@/features/overview/period'
import { useArSummary } from '@/features/invoicing/queries'
import { useApSummary } from '@/features/ap/queries'
import { useApprovalInbox, useApprovalInboxSummary } from '@/features/approvals/queries'
import { useCategoryCounts, useInboxAccounts } from '@/features/inbox/queries'
import { useAuth } from '@/auth/useAuth'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import { FormField } from '@/components/FormField'
import type {
  DashboardCategoryPoint,
  DashboardDailyPoint,
  DashboardTransaction,
  DashboardTrendPoint,
  OverviewCustomRange,
  OverviewPeriod,
  PendingTransaction,
} from '@/features/overview/types'

const PERIOD_LABEL: Record<OverviewPeriod, string> = {
  '1M': 'Last Month',
  '3M': 'Last 3 Months',
  '6M': 'Last 6 Months',
  '1Y': 'Last Year',
  'LFY': 'Last Financial Year',
  'CUSTOM': 'Custom Range',
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function monthAgoIso(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 10)
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  return Number.isFinite(n) ? n : 0
}

function GrowthMeta({
  value,
  invert,
  suffix,
  tone = 'default',
}: {
  value: number
  invert?: boolean
  suffix?: string
  /**
   * Match the parent KpiCard tone so the chip stays legible:
   *   "default" → bg-accent / destructive wash
   *   "hero"    → translucent-white pill on dark brand tile
   *   "soft"    → opaque-white pill on pale brand tile
   */
  tone?: 'default' | 'hero' | 'soft'
}) {
  const isPositive = value >= 0
  const isGood = invert ? !isPositive : isPositive
  const Arrow = isPositive ? ArrowUpRight : ArrowDownRight
  const cls =
    tone === 'hero'
      ? 'bg-white/20 text-primary-foreground'
      : tone === 'soft'
        ? 'bg-white/75 text-accent-foreground'
        : isGood
          ? 'bg-accent text-accent-foreground'
          : 'bg-destructive/10 text-destructive'
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-medium', cls)}
    >
      <Arrow className="size-3" aria-hidden />
      {Math.abs(value)}% {suffix ?? 'vs prev'}
    </span>
  )
}

/**
 * KpiCard - three-tone stat tile.
 *
 *   tone="hero"    → dark brand-strong background, white text. Lead stat.
 *   tone="soft"    → pale brand wash (--bk-accent) background, dark
 *                    brand text. Tinted companion to hero tiles.
 *   tone="default" → plain white card. Use only when the surrounding row
 *                    is already brand-tinted.
 *
 * Icon chip + child text colours auto-adapt per tone so callers never
 * have to think about contrast against whatever brand the tenant picked.
 */
function KpiCard({
  label,
  value,
  icon: Icon,
  meta,
  tone = 'default',
}: {
  label: string
  value: string
  icon: typeof DollarSign
  meta: React.ReactNode
  tone?: 'default' | 'hero' | 'soft'
}) {
  const isHero = tone === 'hero'
  const isSoft = tone === 'soft'
  return (
    <Card
      className={cn(
        'group relative overflow-hidden p-6 transition-shadow hover:shadow-card-hover',
        isHero && 'bg-[hsl(var(--bk-primary-strong))] text-primary-foreground shadow-none hover:shadow-none',
        isSoft && 'bg-accent text-accent-foreground shadow-none hover:shadow-none',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p
          className={cn(
            'text-sm font-medium',
            isHero
              ? 'text-primary-foreground/85'
              : isSoft
                ? 'text-accent-foreground/80'
                : 'text-muted-foreground',
          )}
        >
          {label}
        </p>
        <span
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-pill transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5',
            isHero
              ? 'bg-white/15 text-primary-foreground'
              : isSoft
                ? 'bg-white/70 text-accent-foreground'
                : 'bg-accent text-accent-foreground',
          )}
          aria-hidden
        >
          <Icon className="size-4" />
        </span>
      </div>
      <p
        className={cn(
          'mt-6 truncate text-4xl font-semibold leading-none tracking-tight',
          isHero
            ? 'text-primary-foreground'
            : isSoft
              ? 'text-accent-foreground'
              : 'text-foreground',
        )}
      >
        {value}
      </p>
      <div className={cn('mt-4', isHero && '[&_*]:!text-primary-foreground/85')}>{meta}</div>
    </Card>
  )
}

function StatsRow({
  income,
  incomeGrowth,
  expenses,
  expenseGrowth,
  netIncome,
  profitMargin,
  savingsRate,
}: {
  income: number
  incomeGrowth: number
  expenses: number
  expenseGrowth: number
  netIncome: number
  profitMargin: number
  savingsRate: number
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        tone="hero"
        label="Total Income"
        value={formatCurrency(income)}
        icon={DollarSign}
        meta={<GrowthMeta value={incomeGrowth} tone="hero" />}
      />
      <KpiCard
        tone="soft"
        label="Total Expenses"
        value={formatCurrency(expenses)}
        icon={CreditCard}
        meta={<GrowthMeta value={expenseGrowth} invert tone="soft" />}
      />
      <KpiCard
        tone="hero"
        label="Net Profit"
        value={formatCurrency(netIncome)}
        icon={TrendingUp}
        meta={
          <span className="inline-flex items-center gap-1 rounded-pill bg-white/20 px-2 py-0.5 text-xs font-medium text-primary-foreground">
            {profitMargin}% margin
          </span>
        }
      />
      <KpiCard
        tone="soft"
        label="Savings Rate"
        value={`${savingsRate}%`}
        icon={PiggyBank}
        meta={<p className="text-xs text-accent-foreground/75">Of total income</p>}
      />
    </div>
  )
}

function WeeklyCashFlowCard({ daily }: { daily: DashboardDailyPoint[] }) {
  const { incomeSeries, expenseSeries } = useMemo(() => {
    const income: Array<number | null> = new Array(7).fill(null)
    const expense: Array<number | null> = new Array(7).fill(null)
    daily.forEach((d) => {
      const idx = toNum(d.day_num) - 1
      if (idx < 0 || idx > 6) return
      const inc = toNum(d.income)
      const exp = toNum(d.expense)
      income[idx] = inc > 0 ? inc : null
      expense[idx] = exp > 0 ? exp : null
    })
    return { incomeSeries: income, expenseSeries: expense }
  }, [daily])

  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader title="Average Weekly Cash Flow" />
      <div className="mb-3 flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-full" style={{ background: CHART_COLORS.expenseBar }} />
          Expenses
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-full" style={{ background: CHART_COLORS.incomeBar }} />
          Income
        </span>
      </div>
      <BarChart
        labels={[...DAY_LABELS]}
        series={[
          { label: 'Expenses (Avg)', values: expenseSeries, color: CHART_COLORS.expenseBar },
          { label: 'Income (Avg)', values: incomeSeries, color: CHART_COLORS.incomeBar },
        ]}
        height={280}
        logarithmic
        rotateDataLabels
        formatTooltip={(label, value) => `${label}: ${formatCurrency(value)}`}
        ariaLabel="Average weekly cash flow"
      />
    </Card>
  )
}

function TrendCard({ trends }: { trends: DashboardTrendPoint[] }) {
  const labels = trends.map((t) => t.label)
  const incomeSeries = trends.map((t) => toNum(t.income))
  const expenseSeries = trends.map((t) => toNum(t.expense))
  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader title="Revenue vs Expenses Trend" />
      <LineChart
        labels={labels}
        series={[
          { label: 'Income', values: incomeSeries, color: CHART_COLORS.primary, fill: true },
          { label: 'Expenses', values: expenseSeries, color: CHART_COLORS.secondary, dashed: true },
        ]}
        height={320}
        ariaLabel="Revenue vs expenses trend"
      />
    </Card>
  )
}

function CategoryBreakdownCard({ categories }: { categories: DashboardCategoryPoint[] }) {
  const items = categories.map((c) => ({ category: c.category, total: toNum(c.total) }))
  const hasData = items.length > 0
  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader
        title="Expense Breakdown"
        action={<SectionLink to="/reports" size="sm">View reports</SectionLink>}
      />
      {hasData ? (
        <>
          <DoughnutChart
            labels={items.map((c) => c.category)}
            values={items.map((c) => c.total)}
            height={220}
            ariaLabel="Expense breakdown by category"
          />
          <div className="mt-4 border-t border-border pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Top 5 Categories</p>
            <ul className="flex flex-col gap-2">
              {items.map((c, i) => (
                <li
                  key={c.category}
                  className="flex items-center justify-between border-b border-border/60 pb-1.5 text-sm last:border-b-0"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="inline-block size-2.5 shrink-0 rounded-full"
                      style={{ background: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }}
                    />
                    <span className="truncate font-medium text-foreground">{c.category}</span>
                  </span>
                  <span className="font-semibold text-foreground">{formatCurrency(c.total)}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">No data available</div>
      )}
    </Card>
  )
}

function RecentTransactionsCard({ txns }: { txns: DashboardTransaction[] }) {
  const columns = useMemo<ColumnDef<DashboardTransaction>[]>(
    () => [
      {
        accessorKey: 'transaction_date',
        header: 'Date',
        cell: ({ row }) => formatDate(row.original.transaction_date),
      },
      {
        accessorKey: 'transaction_details',
        header: 'Description',
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.transaction_details}</span>
        ),
      },
      {
        accessorKey: 'amount',
        header: () => <span className="block text-right">Amount</span>,
        cell: ({ row }) => {
          const n = toNum(row.original.amount)
          return (
            <span className={cn('block text-right font-medium', n < 0 ? 'text-destructive' : 'text-foreground')}>
              {formatCurrency(n)}
            </span>
          )
        },
      },
      {
        accessorKey: 'status',
        header: () => <span className="block text-center">Status</span>,
        cell: ({ row }) => (
          <span className="block text-center">
            <Badge variant="default">{row.original.status ?? '-'}</Badge>
          </span>
        ),
      },
    ],
    [],
  )
  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader
        title="Recent Transactions"
        action={<SectionLink to="/view-statements">View all</SectionLink>}
      />
      {/* Cap at ~6 rows then scroll - keeps the card at a predictable
          height no matter how many transactions come back. */}
      <div className="max-h-96 overflow-auto rounded-md border border-border">
        <DataTable
          columns={columns}
          rows={txns}
          sortable={false}
          empty="No transactions."
        />
      </div>
    </Card>
  )
}

function OperationalInsightsCard({
  dailySpend,
  projected,
  pendingCount,
  cashPositive,
  income,
  expenses,
  netIncome,
  onPendingClick,
}: {
  dailySpend: number
  projected: number
  pendingCount: number
  cashPositive: boolean
  income: number
  expenses: number
  netIncome: number
  onPendingClick: () => void
}) {
  // Meaningful progress %: spend vs income for the period.
  const spendPct =
    income > 0 ? Math.min(100, Math.round((expenses / income) * 100)) : 0
  const runRatePct =
    income > 0 ? Math.min(100, Math.round((projected / income) * 100)) : 0

  return (
    <Card className="flex h-full flex-col p-4 sm:p-5">
      <SectionHeader
        title="Operational Insights"
        action={<SectionLink to="/analyze-statements" size="sm">Deep dive</SectionLink>}
      />
      <div className="flex flex-1 flex-col gap-4">
        <div>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Avg. Daily Spend</span>
            <span className="font-semibold text-foreground">{formatCurrency(dailySpend)}</span>
          </div>
          <div className="h-2 w-full rounded-pill bg-muted">
            <div
              className="h-2 rounded-pill bg-primary transition-all"
              style={{ width: `${spendPct}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {spendPct}% of income flowing out as expenses.
          </p>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Projected Monthly Run Rate</span>
            <span className="font-semibold text-foreground">{formatCurrency(projected)}</span>
          </div>
          <div className="h-2 w-full rounded-pill bg-muted">
            <div
              className={cn(
                'h-2 rounded-pill transition-all',
                runRatePct >= 90
                  ? 'bg-destructive'
                  : runRatePct >= 70
                    ? 'bg-warning'
                    : 'bg-success',
              )}
              style={{ width: `${runRatePct}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {runRatePct}% of income, projected forward.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onPendingClick}
            className="h-auto flex-col items-start gap-0 rounded-md border border-border bg-muted/60 px-4 py-3 text-left hover:bg-muted"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending</span>
            <span className="mt-1 text-lg font-bold text-foreground">{pendingCount}</span>
            <span className="text-[0.7rem] text-primary">Click to view</span>
          </Button>
          <div className="flex flex-col rounded-md border border-border bg-muted/60 px-4 py-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cash Position</span>
            <span className={cn('mt-1 text-lg font-bold', cashPositive ? 'text-foreground' : 'text-destructive')}>
              {cashPositive ? 'Positive' : 'Negative'}
            </span>
            <span className="text-[0.7rem] text-muted-foreground">
              Net {formatCurrency(netIncome)}
            </span>
          </div>
        </div>

        {/* Quick actions - fills the previous empty space with jumps to
            the high-intent workflows from here. */}
        <div className="mt-auto border-t border-border pt-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Quick actions
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/upload">Upload statement</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/invoicing/invoices/new">New invoice</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/reports">Run a report</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/reconcile">Reconcile</Link>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

function PendingModal({
  open,
  onOpenChange,
  pending,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  pending: PendingTransaction[] | undefined
}) {
  const list = pending ?? []
  const allDone = open && pending !== undefined && list.length === 0
  const invalidate = useInvalidateDashboard()

  // When the list empties while open, refresh dashboard metrics and close
  // the modal shortly after - matches legacy behavior minus the hard reload.
  useEffect(() => {
    if (!allDone) return
    const t = window.setTimeout(() => {
      invalidate()
      onOpenChange(false)
    }, 1500)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone])

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={allDone ? 'All Done!' : `Pending Transactions (${list.length})`}
      description={allDone ? undefined : 'Processing in background...'}
    >
      {allDone ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <CheckCircle2 className="size-12 text-success" aria-hidden />
          <p className="text-muted-foreground">All transactions have been processed.</p>
        </div>
      ) : list.length === 0 ? (
        <EmptyState title="Loading…" description="Fetching pending transactions." />
      ) : (
        <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
          <DataTable
            columns={[
              {
                accessorKey: 'transaction_date',
                header: 'Date',
                cell: ({ row }) => formatDate(row.original.transaction_date),
              },
              {
                accessorKey: 'transaction_details',
                header: 'Details',
              },
              {
                accessorKey: 'amount',
                header: () => <span className="block text-right">Amount</span>,
                cell: ({ row }) => (
                  <span className="block text-right font-medium">
                    {formatCurrency(toNum(row.original.amount))}
                  </span>
                ),
              },
            ] satisfies ColumnDef<PendingTransaction>[]}
            rows={list}
            sortable={false}
          />
        </div>
      )}
    </Modal>
  )
}

function BudgetDashboardSection({ range }: { range: { from: string; to: string } }) {
  const { data } = useBudgetDashboardSummary(range)
  if (!data || data.has_budget === false) return null

  // Healthy / nominal values render in the regular foreground colour so
  // the dashboard inherits whatever text colour the tenant theme uses
  // (and flips correctly in dark mode). Only the "something's wrong"
  // states keep their semantic red/amber so they still grab attention.
  const percentColor =
    data.percent_used >= 100
      ? 'text-destructive'
      : data.percent_used >= 80
        ? 'text-warning-foreground'
        : 'text-foreground'
  const remainingColor = data.total_remaining >= 0 ? 'text-foreground' : 'text-destructive'

  const alerts = [
    ...data.over_budget.map((a) => ({ ...a, kind: 'over' as const })),
    ...data.warnings.map((a) => ({ ...a, kind: 'warn' as const })),
  ].slice(0, 4)

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card className="p-4 sm:p-5">
        <SectionHeader
          title="Budget Health"
          action={<SectionLink to="/budget-planning">View details</SectionLink>}
        />
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-md border border-border bg-muted/60 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Budget</div>
            <div className="mt-1 text-lg font-bold text-foreground">{formatCurrency(data.total_budgeted)}</div>
          </div>
          <div className="rounded-md border border-border bg-muted/60 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Spent</div>
            <div className="mt-1 text-lg font-bold text-foreground">{formatCurrency(data.total_spent)}</div>
          </div>
          <div className="rounded-md border border-border bg-muted/60 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Remaining</div>
            <div className={cn('mt-1 text-lg font-bold', remainingColor)}>
              {formatCurrency(data.total_remaining)}
            </div>
          </div>
          <div className="rounded-md border border-border bg-muted/60 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">% Used</div>
            <div className={cn('mt-1 text-lg font-bold', percentColor)}>{data.percent_used}%</div>
          </div>
        </div>
        {alerts.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {alerts.map((a) => {
              const tone =
                a.kind === 'over' ? 'bg-destructive/10 text-destructive' : 'bg-warning/15 text-warning-foreground'
              const label = a.kind === 'over' ? 'Over Budget' : 'Warning'
              return (
                <div
                  key={`${a.kind}-${a.category}`}
                  className={cn('flex items-center justify-between rounded-md px-3 py-1.5 text-sm', tone)}
                >
                  <span className="truncate font-semibold">{a.category}</span>
                  <span className="shrink-0 font-medium">
                    {label} ({a.percent}%)
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </Card>
      <Card className="p-4 sm:p-5">
        <SectionHeader
          title="Upcoming Payments"
          action={<SectionLink to="/budget-planning">Manage</SectionLink>}
        />
        {data.upcoming_payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming payments</p>
        ) : (
          // Cap at ~4 rows then scroll inside the card.
          <div className="max-h-72 overflow-y-auto pr-1">
            <ul className="flex flex-col gap-2">
              {data.upcoming_payments.map((p) => {
                const urgent =
                  p.days_until <= 3
                    ? 'text-destructive'
                    : p.days_until <= 7
                      ? 'text-warning-foreground'
                      : 'text-muted-foreground'
                return (
                  <li
                    key={`${p.name}-${p.next_due_date}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">{p.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {p.category ? `${p.category} • ` : ''}Due {formatDate(p.next_due_date, { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={cn('text-sm font-bold', urgent)}>{formatCurrency(toNum(p.amount))}</div>
                      <div className={cn('text-xs', urgent)}>{p.days_until}d away</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
        {data.recent_alerts.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent Alerts</p>
            <ul className="flex flex-col gap-0.5">
              {data.recent_alerts.slice(0, 3).map((a, i) => {
                const color =
                  a.severity === 'error'
                    ? 'text-destructive'
                    : a.severity === 'warning'
                      ? 'text-warning-foreground'
                      : 'text-primary'
                return (
                  <li key={String(a.id ?? i)} className={cn('text-sm', color)}>
                    • {a.title}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </Card>
    </div>
  )
}

function ArSummarySection({
  range,
  paidLabel,
}: {
  range: { from: string; to: string }
  paidLabel: string
}) {
  const { hasPermission } = useAuth()
  const canView = hasPermission('invoicing.invoices.view')
  const { data } = useArSummary(range)

  if (!canView) return null

  const outstanding = data?.outstanding_ar ?? 0
  const paidThisMonth = data?.paid_this_month ?? 0
  const overdueCount = data?.overdue_count ?? 0
  const sentCount = data?.sent_count ?? 0
  const draftCount = data?.draft_count ?? 0
  const recent = data?.recent_activity ?? []

  const statusLabel = (to: string, from: string | null) => {
    if (to === 'paid') return 'Paid'
    if (to === 'sent') return 'Sent'
    if (to === 'viewed') return 'Viewed'
    if (to === 'void') return 'Voided'
    if (to === 'draft' && !from) return 'Created'
    return to.replace(/_/g, ' ')
  }

  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader
        title="Invoicing - AR"
        description="Customer invoices and payments for the current tenant."
        action={<SectionLink to="/invoicing/invoices">View invoices</SectionLink>}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <KpiCard
          label="Outstanding AR"
          value={formatCurrency(outstanding)}
          icon={Receipt}
          meta={
            <p className="text-xs text-muted-foreground">
              {sentCount} open · {overdueCount} overdue · {draftCount} drafts
            </p>
          }
        />
        <KpiCard
          label={paidLabel}
          value={formatCurrency(paidThisMonth)}
          icon={CheckCircle2}
          meta={<p className="text-xs text-muted-foreground">Settled customer payments</p>}
        />
      </div>
      {recent.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Recent invoicing activity
          </p>
          <ul className="flex max-h-60 flex-col gap-1.5 overflow-y-auto pr-1">
            {recent.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <Link
                    to={`/invoicing/invoices/${a.invoice_id}`}
                    className="truncate font-medium text-foreground hover:underline"
                  >
                    {a.invoice_number}
                  </Link>
                  <span className="truncate text-muted-foreground">· {a.customer_display_name}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Badge variant={a.to_status === 'paid' ? 'success' : a.to_status === 'void' ? 'secondary' : 'default'}>
                    {statusLabel(a.to_status, a.from_status)}
                  </Badge>
                  <span className="font-semibold text-foreground">
                    {formatCurrency(toNum(a.total))}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

export function OverviewPage() {
  const [period, setPeriod] = useState<OverviewPeriod>('1M')
  const [pendingOpen, setPendingOpen] = useState(false)
  const regional = useRegionalDefaults()
  const [customRange, setCustomRange] = useState<OverviewCustomRange>(() => ({
    from: monthAgoIso(),
    to: todayIso(),
  }))
  const { data, isLoading, isError, error, refetch } = useDashboardSummary(
    period,
    period === 'CUSTOM' ? customRange : undefined,
    period === 'LFY' ? regional.fiscalStartMonth : undefined,
  )
  const pendingQuery = usePendingDetails(pendingOpen)

  // Resolved window so AR / AP / Budget summaries reflect the same
  // period as the main dashboard tiles. For CUSTOM with an incomplete
  // range we leave the window undefined so those queries keep showing
  // their default month-to-date numbers until both dates are filled.
  const periodWindow = useMemo(() => {
    if (period === 'CUSTOM' && (!customRange.from || !customRange.to)) return undefined
    return resolvePeriodWindow(
      period,
      period === 'CUSTOM' ? customRange : undefined,
      period === 'LFY' ? regional.fiscalStartMonth : undefined,
    )
  }, [period, customRange, regional.fiscalStartMonth])
  const paidLabel = period === '1M' ? 'Paid this month' : `Paid in period (${PERIOD_LABEL[period]})`

  const metrics = data?.metrics
  const income = toNum(metrics?.income)
  const expenses = toNum(metrics?.expenses)
  const incomeGrowth = toNum(metrics?.incomeGrowth)
  const expenseGrowth = toNum(metrics?.expenseGrowth)
  const netIncome = toNum(metrics?.netIncome)
  const profitMargin = toNum(metrics?.profitMargin)
  const savingsRate = income > 0 ? +((netIncome / income) * 100).toFixed(1) : 0

  const actions = (
    <div className="flex flex-wrap items-end gap-2">
      <Select value={period} onValueChange={(v) => setPeriod(v as OverviewPeriod)}>
        <SelectTrigger className="w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(PERIOD_LABEL) as OverviewPeriod[]).map((p) => (
            <SelectItem key={p} value={p}>
              {PERIOD_LABEL[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {period === 'CUSTOM' ? (
        <>
          <FormField
            label="From"
            type="date"
            value={customRange.from}
            max={customRange.to || undefined}
            onChange={(e) =>
              setCustomRange((r) => ({ ...r, from: e.currentTarget.value }))
            }
            containerClassName="w-[160px]"
          />
          <FormField
            label="To"
            type="date"
            value={customRange.to}
            min={customRange.from || undefined}
            onChange={(e) => setCustomRange((r) => ({ ...r, to: e.currentTarget.value }))}
            containerClassName="w-[160px]"
          />
        </>
      ) : null}
    </div>
  )

  return (
    <Page
      title="Overview"
      description="Financial snapshot with income, expenses, and category breakdowns for the selected period."
      actions={actions}
      width="wide"
    >
      {isError ? (
        <Card className="p-6">
          <EmptyState
            title="Couldn't load dashboard"
            description={error instanceof Error ? error.message : 'Unknown error'}
            action={
              <Button type="button" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <StatsRow
            income={income}
            incomeGrowth={incomeGrowth}
            expenses={expenses}
            expenseGrowth={expenseGrowth}
            netIncome={netIncome}
            profitMargin={profitMargin}
            savingsRate={savingsRate}
          />

          {periodWindow ? (
            <>
              <ArSummarySection range={periodWindow} paidLabel={paidLabel} />
              <ApSummaryCard range={periodWindow} paidLabel={paidLabel} />
            </>
          ) : null}
          <ApprovalsInboxCard />
          <InboxSummaryCard />

          {isLoading || !data ? (
            <Card className="p-8">
              <EmptyState title="Loading dashboard…" description="Fetching the latest data." />
            </Card>
          ) : (
            <>
              <WeeklyCashFlowCard daily={data.charts.daily} />
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <div className="lg:col-span-3">
                  <TrendCard trends={data.charts.trends} />
                </div>
                <div className="lg:col-span-1">
                  <CategoryBreakdownCard categories={data.charts.categories} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <RecentTransactionsCard txns={data.recentTransactions} />
                <OperationalInsightsCard
                  dailySpend={toNum(data.insights.dailySpend)}
                  projected={toNum(data.insights.projected)}
                  pendingCount={data.metrics.pendingItems}
                  cashPositive={netIncome >= 0}
                  income={income}
                  expenses={expenses}
                  netIncome={netIncome}
                  onPendingClick={() => setPendingOpen(true)}
                />
              </div>
              {periodWindow ? <BudgetDashboardSection range={periodWindow} /> : null}
            </>
          )}

          <PendingModal
            open={pendingOpen}
            onOpenChange={setPendingOpen}
            pending={pendingQuery.data}
          />
        </>
      )}
    </Page>
  )
}

/**
 * Phase 4 Dashboard card - AP summary mirror of the AR widget.
 * Shows outstanding AP, paid this month, count of bills pending
 * approval. Renders nothing if the tenant has no bill data yet.
 */
function ApSummaryCard({
  range,
  paidLabel,
}: {
  range: { from: string; to: string }
  paidLabel: string
}) {
  const apQ = useApSummary(range)
  const ap = apQ.data
  if (apQ.isLoading) return null
  if (!ap) return null
  // Hide entirely when there's no AP signal to show
  if (ap.outstanding_ap === 0 && ap.paid_this_month === 0 && ap.pending_approval_count === 0 && ap.open_count === 0) return null
  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader
        title="Invoicing - Accounts Payable"
        description="Bills you owe. Numbers reconcile to the AP control account on the Balance Sheet."
        action={<SectionLink to="/invoicing/bills">View bills</SectionLink>}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Outstanding AP"
          value={formatCurrency(toNum(ap.outstanding_ap))}
          icon={CreditCard}
          meta={<p className="text-xs text-muted-foreground">Open bills awaiting payment</p>}
        />
        <KpiCard
          label={paidLabel}
          value={formatCurrency(toNum(ap.paid_this_month))}
          icon={CheckCircle2}
          meta={<p className="text-xs text-muted-foreground">Settled vendor payments</p>}
        />
        <KpiCard
          label="Open bills"
          value={String(ap.open_count)}
          icon={Receipt}
          meta={
            <p className="text-xs text-muted-foreground">
              {ap.overdue_count
                ? <span className="text-destructive">{ap.overdue_count} overdue</span>
                : 'None overdue'}
            </p>
          }
        />
        <KpiCard
          label="Pending approval"
          value={String(ap.pending_approval_count)}
          icon={FileText}
          meta={<p className="text-xs text-muted-foreground">Awaiting approver action</p>}
        />
      </div>
    </Card>
  )
}

/**
 * Phase 3d Dashboard card - "Pending my approval".
 *
 * Uses both the summary count (for the big number) and the full inbox
 * list so the card can show up to 3 previews. Cheap: the TanStack
 * queries cache across the page and the inbox call is already bounded
 * by the server.
 */
function InboxSummaryCard() {
  const { hasPermission } = useAuth()
  const canView = hasPermission('inbox.view')
  const countsQ = useCategoryCounts()
  const accountsQ = useInboxAccounts()
  if (!canView) return null
  if (countsQ.isLoading) return null
  const counts = countsQ.data?.counts ?? []
  const total = countsQ.data?.total ?? 0
  const accounts = accountsQ.data?.accounts ?? []
  const importing = accounts.filter((a) => a.backfill_status === 'running')
  const importedSoFar = importing.reduce((n, a) => n + (a.backfill_imported ?? 0), 0)
  const importTarget = importing.reduce((n, a) => n + (a.backfill_total ?? 0), 0)
  if (total === 0 && importing.length === 0) return null
  const financial = counts.filter((c) => c.is_financial && c.count > 0)
  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            Inbox
            <Badge variant="default">{total}</Badge>
          </span>
        }
        description="Emails synced and categorized. Nothing posts until you accept it."
        action={<SectionLink to="/inbox">Open inbox</SectionLink>}
      />
      {importing.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          Importing your mailbox… {importedSoFar.toLocaleString()}
          {importTarget > 0 ? ` / ${importTarget.toLocaleString()}` : ''} emails
        </p>
      ) : null}
      {financial.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {financial.map((c) => (
            <div key={c.category_id} className="rounded-md border border-border p-3">
              <div className="text-xs text-muted-foreground">{c.name}</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight">{c.count}</div>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  )
}

function ApprovalsInboxCard() {
  const summaryQ = useApprovalInboxSummary()
  const inboxQ = useApprovalInbox()
  const count = summaryQ.data?.pending_count ?? 0
  const items = (inboxQ.data?.items ?? []).slice(0, 3)
  if (summaryQ.isLoading) return null
  if (count === 0 && items.length === 0) return null // nothing to show, collapse
  return (
    <Card className="p-4 sm:p-5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            Pending my approval
            <Badge variant="default">{count}</Badge>
          </span>
        }
        description="Approval requests waiting on you (direct + via delegation)."
        action={<SectionLink to="/approvals">Go to inbox</SectionLink>}
      />
      {items.length > 0 ? (
        <ul className="flex flex-col gap-2 text-sm">
          {items.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-2">
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  <Link to={`/approvals/instances/${i.id}`} className="font-medium text-primary hover:underline">
                    #{i.id}
                  </Link>
                  <span className="ml-2 text-xs text-muted-foreground">{i.resource_type}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">{i.resource_summary || '-'}</div>
              </div>
              <SectionLink to={`/approvals/instances/${i.id}`} size="sm">
                Review
              </SectionLink>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  )
}
